// Сводка отвечает на «что делал агент / что ждёт меня / что с деньгами / что горит» (ревью 26.09, §8):
// сверху — вывод агента из последнего брифинга, обхода или итога дня; дальше — первые строки очереди «Ждёт вас»
// с кнопками прямо здесь; затем полоса KPI за сегодня (сравнение со вчера к этому часу считает api), тревоги
// и ближайшие запуски агента. На телефоне тот же порядок. Пока ничего не подключено — чек-лист первого запуска.
// Цифры считает api (/api/summary); страница только показывает.
import {
  Button,
  Card,
  Chip,
  int,
  pct,
  Skeleton,
  Sparkline,
  signedUsd,
  type Tone,
  time,
  usd,
  when,
} from '@autoaff/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Bot,
  Check,
  Circle,
  CircleAlert,
  HelpCircle,
  Info,
  MessageCircle,
  TriangleAlert,
} from 'lucide-react';
import type { FeedRun, SchedulesResponse } from '../agent-api';
import { type AlertView, api } from '../api';
import { type SummaryData, useIntegrations, useSummary } from '../business';
import { chatStore } from '../chat/store';
import { askOfRun, useWaiting } from '../shell/waiting';
import { useAgentStatus } from '../streams';
import { PageHead, todayLine } from './common';
import { WaitingRows } from './Decisions';

export function SummaryPage() {
  const q = useSummary();
  const d = q.data;
  const nothing = d && !d.connected.buyo && !d.connected.meta;
  return (
    <>
      <PageHead
        title="Сводка"
        sub={todayLine()}
        actions={
          <Button className="hide-m" icon={<MessageCircle />} onClick={() => chatStore.open()}>
            Спросить агента
          </Button>
        }
      />
      <div className="pc">
        {nothing ? (
          <FirstRun />
        ) : (
          <div className="sum">
            <AgentNote />
            <Waiting />
            <div className="sum-kpis">
              <TodayKpis d={d} />
              {d && d.foreign.days > 0 && (
                <p className="muted tnum sum-foot">
                  Кампании друга не учитываются: сегодня {usd(d.foreign.today)}, за 14 дней{' '}
                  {usd(d.foreign.days)}
                  {d.spendSyncedAt ? ` · расход обновлён ${when(d.spendSyncedAt)}` : ''}
                </p>
              )}
            </div>
            <div className="sum-side">
              {d && (!d.connected.buyo || !d.connected.meta) && <Connections d={d} />}
              <Alerts />
              <Upcoming />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── вывод агента ───────────────────────────────────────────────────────
const NOTE_KINDS = ['briefing', 'round', 'day_summary', 'weekly', 'monthly'];

/** Markdown брифинга → простой текст для превью в 2–3 строки (полностью — в ленте). */
function plainText(md: string): string {
  return md
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[*_`>#]+/g, '')
    .replace(/^\s*[-•]\s+/gm, '')
    .replace(/\s*\n+\s*/g, ' ')
    .trim();
}

function AgentNote() {
  const q = useQuery({
    queryKey: ['agent', 'feed', 'note'],
    queryFn: () =>
      api<FeedRun[]>(`/api/agent/feed?limit=1&report=1&kinds=${NOTE_KINDS.join(',')}`).then(
        // Старый api без фильтра отдаёт всю ленту — берём первый итог нужного вида
        (rows) => rows.find((r) => r.report && NOTE_KINDS.includes(r.kind)) ?? null,
      ),
    refetchInterval: 60_000,
  });
  const sch = useQuery({
    queryKey: ['agent', 'schedules'],
    queryFn: () => api<SchedulesResponse>('/api/agent/schedules'),
    enabled: q.isSuccess && !q.data,
  });
  const r = q.data;
  const asks = r?.report?.asks_owner ?? [];
  if (q.isPending)
    return (
      <section className="card sum-agent" aria-label="Вывод агента">
        <Skeleton w="40%" />
        <Skeleton w="80%" h={22} />
        <Skeleton w="95%" />
        <Skeleton w="70%" />
      </section>
    );
  if (q.isError)
    return (
      <section className="card sum-agent" aria-label="Вывод агента">
        <div className="sum-agent-head">
          <Bot aria-hidden="true" />
          <span>Вывод агента</span>
        </div>
        <p className="sum-agent-empty">Не удалось загрузить последний итог агента.</p>
        <div className="sum-agent-foot">
          <Button size="sm" variant="ghost" onClick={() => void q.refetch()}>
            Повторить
          </Button>
        </div>
      </section>
    );
  if (!r?.report) {
    const brief = sch.data?.schedules.find((s) => s.kind === 'briefing' && s.nextAt);
    return (
      <section className="card sum-agent" aria-label="Вывод агента">
        <div className="sum-agent-head">
          <Bot aria-hidden="true" />
          <span>Вывод агента</span>
        </div>
        <p className="sum-agent-empty">
          Агент ещё не подводил итогов.
          {brief?.nextAt ? ` Первый брифинг — ${when(brief.nextAt)}.` : ''} Каждое утро, в обходы и вечером он
          пишет здесь, что увидел в цифрах и что сделал.
        </p>
      </section>
    );
  }
  return (
    <section className="card sum-agent" aria-label="Вывод агента">
      <div className="sum-agent-head">
        <Bot aria-hidden="true" />
        <span>
          {r.kindLabel} · {when(r.endedAt ?? r.startedAt ?? r.createdAt)}
        </span>
      </div>
      <h2 className="sum-agent-title">{r.report.headline}</h2>
      <p className="sum-agent-text">{plainText(r.report.summary)}</p>
      <div className="sum-agent-foot">
        <Link to="/agent/feed" className="btn btn-sm btn-ghost">
          Читать <ArrowRight />
        </Link>
        {asks.length > 0 && r.threadId && (
          <Button
            size="sm"
            variant="ghost"
            icon={<HelpCircle />}
            onClick={() => {
              const ask = askOfRun(r);
              chatStore.open(ask ? { ask } : { threadId: r.threadId });
            }}
          >
            Вопросы к вам · {asks.length}
          </Button>
        )}
      </div>
    </section>
  );
}

// ─── ждут решения ───────────────────────────────────────────────────────
function Waiting() {
  const q = useWaiting();
  const total = q.data?.total ?? 0;
  return (
    <Card
      className="sum-wait"
      title="Ждут решения"
      count={total || undefined}
      action={
        total > 3 ? (
          <Link to="/decisions" className="btn btn-sm btn-ghost">
            Все {int(total)} <ArrowRight />
          </Link>
        ) : total > 0 ? (
          <Link to="/decisions" className="btn btn-sm btn-ghost">
            Открыть <ArrowRight />
          </Link>
        ) : undefined
      }
    >
      {q.isPending && (
        <div className="flex flex-col gap-3">
          <Skeleton h={40} />
          <Skeleton h={40} />
        </div>
      )}
      {q.isError && (
        <p className="sum-calm muted">
          Не удалось загрузить очередь.{' '}
          <button type="button" className="link-btn" onClick={() => void q.refetch()}>
            Повторить
          </button>
        </p>
      )}
      {q.isSuccess && total === 0 && (
        <p className="sum-calm">
          <Check aria-hidden="true" /> Сейчас ничего не ждёт вашего решения.
        </p>
      )}
      {q.isSuccess && total > 0 && <WaitingRows items={q.data.items.slice(0, 3)} />}
    </Card>
  );
}

// ─── цифры за сегодня ───────────────────────────────────────────────────
/** Изменение к вчерашнему дню на этот же час: стрелка, цвет — только где рост однозначно хорош. */
function Delta({ now, then, good }: { now: number; then: number | null; good?: 'up' }) {
  if (then === null) return null;
  if (then === 0) return <>{now === 0 ? 'как вчера к этому часу' : 'вчера к этому часу было 0'}</>;
  const diff = now - then;
  const Icon = diff >= 0 ? ArrowUp : ArrowDown;
  const tone = good === 'up' && diff !== 0 ? (diff > 0 ? 'pos' : 'neg') : '';
  return (
    <span className={`delta ${tone}`}>
      <Icon aria-hidden="true" />
      {pct(Math.abs((diff / then) * 100), 0)} к вчера в это время
    </span>
  );
}

function TodayKpis({ d }: { d: SummaryData | undefined }) {
  if (!d)
    return (
      <section className="kpis" aria-label="Сегодня">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="kpi">
            <Skeleton w="50%" />
            <Skeleton w="70%" h={26} />
          </div>
        ))}
      </section>
    );
  const t = d.today;
  const series = (k: 'spend' | 'leads' | 'approved' | 'profit' | 'forecastProfit') => d.days.map((x) => x[k]);
  const profitTone = (n: number) => (n > 0 ? 'pos' : n < 0 ? 'neg' : undefined);
  return (
    <section className="kpis" aria-label="Сегодня">
      <div className="kpi">
        <span>Расход сегодня</span>
        <b>{usd(t.spend)}</b>
        <small>
          <Delta now={t.spend} then={d.sameTimeYesterday.spend} />
          {d.sameTimeYesterday.spend === null && `вчера за день ${usd(d.yesterday.spend)}`}
        </small>
        <Sparkline values={series('spend')} tone="accent" />
      </div>
      <div className="kpi">
        <span>Лиды</span>
        <b>{int(t.leads)}</b>
        <small>
          <Delta now={t.leads} then={d.sameTimeYesterday.leads} good="up" />
        </small>
        {t.cpl !== null && <small>CPL {usd(t.cpl)}</small>}
        <Sparkline values={series('leads')} tone="accent" />
      </div>
      <div className="kpi">
        <span>Апрувы</span>
        <b>{int(t.approved)}</b>
        <small>
          {d.rates.approveRate !== null
            ? `${pct(d.rates.approveRate)} — апрув от решённых за 30 дней`
            : 'решённых лидов за 30 дней пока нет'}
        </small>
        <Sparkline values={series('approved')} tone="success" />
      </div>
      <div className="kpi">
        <span>Прибыль</span>
        <b className={profitTone(t.profit)}>{signedUsd(t.profit)}</b>
        <small>
          выплаты {usd(t.revenue)} · вчера {signedUsd(d.yesterday.profit)}
        </small>
        <Sparkline values={series('profit')} tone={t.profit < 0 ? 'danger' : 'success'} />
      </div>
      <div className="kpi">
        <span>Прогноз прибыли</span>
        <b className={profitTone(t.forecastProfit)}>{signedUsd(t.forecastProfit)}</b>
        <small>
          {t.inProcess > 0
            ? `+${usd(t.expected)} ждём от ${int(t.inProcess)} в обработке`
            : 'все лиды с решением'}
        </small>
        <Sparkline values={series('forecastProfit')} tone={t.forecastProfit < 0 ? 'danger' : 'success'} />
      </div>
    </section>
  );
}

// ─── тревоги ────────────────────────────────────────────────────────────
const RANK: Record<AlertView['severity'], number> = { critical: 0, warning: 1, info: 2 };
const ALERT_ICON: Record<AlertView['severity'], typeof Info> = {
  critical: CircleAlert,
  warning: TriangleAlert,
  info: Info,
};

/** Ссылка действия алерта: путь, параметры (?show=risky) и якорь (#integrations). */
function AlertLink({ a }: { a: AlertView }) {
  if (!a.action) return null;
  const [pathQ = '', hash] = a.action.to.split('#');
  const [path, qs] = pathQ.split('?');
  if (!path) return null;
  const search = qs ? Object.fromEntries(new URLSearchParams(qs)) : undefined;
  return (
    <Link to={path} hash={hash} search={search as never} className="sum-alert-go">
      {a.action.label}
    </Link>
  );
}

function Alerts() {
  const { status } = useAgentStatus();
  const alerts = [...(status?.alerts ?? [])].sort((a, b) => RANK[a.severity] - RANK[b.severity]);
  return (
    <Card title="Тревоги" count={alerts.length || undefined}>
      {!status && <Skeleton h={36} />}
      {status && alerts.length === 0 && (
        <p className="sum-calm">
          <Check aria-hidden="true" /> Всё спокойно — страховка и проверки платформы следят.
        </p>
      )}
      {alerts.length > 0 && (
        <ul className="sum-alerts">
          {alerts.slice(0, 4).map((a) => {
            const Icon = ALERT_ICON[a.severity];
            return (
              <li key={a.id} data-sev={a.severity}>
                <Icon aria-hidden="true" />
                <div>
                  <b>{a.title}</b>
                  <span>{when(a.createdAt)}</span>
                </div>
                <AlertLink a={a} />
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ─── ближайшие запуски ──────────────────────────────────────────────────
function Upcoming() {
  const q = useQuery({
    queryKey: ['agent', 'schedules'],
    queryFn: () => api<SchedulesResponse>('/api/agent/schedules'),
    refetchInterval: 60_000,
  });
  const next = [
    ...(q.data?.schedules ?? []).flatMap((s) =>
      s.enabled && s.nextAt ? [{ key: `s${s.id}`, at: s.nextAt, title: s.title, self: false }] : [],
    ),
    ...(q.data?.followups ?? []).map((f) => ({
      key: `f${f.id}`,
      at: f.dueAt,
      title: f.prompt,
      self: true,
    })),
  ]
    .sort((a, b) => a.at.localeCompare(b.at))
    .slice(0, 3);
  return (
    <Card
      title="Ближайшие запуски"
      action={
        <Link to="/agent/tasks" hash="schedule" className="btn btn-sm btn-ghost">
          Расписание
        </Link>
      }
    >
      {q.isPending && <Skeleton h={36} />}
      {q.isError && <p className="sum-calm muted">Не удалось загрузить расписание.</p>}
      {q.isSuccess && next.length === 0 && (
        <p className="sum-calm muted">Расписание выключено — агент работает только по вашим задачам.</p>
      )}
      {next.length > 0 && (
        <ul className="sum-runs">
          {next.map((n) => (
            <li key={n.key}>
              <time className="tnum" dateTime={n.at}>
                {isToday(n.at) ? time(n.at) : when(n.at)}
              </time>
              <span>
                {n.self && <Chip>проверка</Chip>} {n.title}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

const isToday = (iso: string) => when(iso).startsWith('сегодня');

// ─── подключения и первый запуск ────────────────────────────────────────
function Connections({ d }: { d: SummaryData }) {
  const rows = [
    { name: 'Сеть buyo', ok: d.connected.buyo, anchor: 'buyo' },
    { name: 'Реклама Meta', ok: d.connected.meta, anchor: 'meta' },
  ];
  return (
    <Card title="Подключения">
      <ul className="conn-list">
        {rows.map((r) => (
          <li key={r.name}>
            {r.ok ? <Check className="pos" aria-hidden="true" /> : <Circle aria-hidden="true" />}
            <span>{r.name}</span>
            {r.ok ? (
              <Chip tone="success">Подключено</Chip>
            ) : (
              <Link to="/settings" hash={r.anchor} className="btn btn-sm">
                Подключить
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

interface Step {
  title: string;
  text: string;
  state: 'done' | 'next' | 'later';
  tag: string;
}

/** Первый запуск: ничего ещё не подключено — объясняем, что даст каждое подключение. */
function FirstRun() {
  const { status } = useAgentStatus();
  const integ = useIntegrations();
  const lost = status?.access?.state === 'lost';
  const steps: Step[] = [
    {
      title: 'Агент на вашей подписке Claude',
      text: !status?.runner
        ? 'Сервис запуска не подключён. Проверьте, что он запущен.'
        : lost
          ? 'Claude не принимает токен подписки. Вставьте новый в настройках — агент продолжит сам.'
          : 'Сервис запуска подключён — агент отвечает в чате и просит одобрения на действия.',
      state: status?.runner && !lost ? 'done' : 'next',
      tag: !status?.runner ? 'Нужна проверка' : lost ? 'Нет доступа' : 'Готово',
    },
    {
      title: 'Сеть buyo',
      text: 'Каталог, потоки, лиды и статусы. Понадобится ключ API из кабинета buyo.',
      state: integ.data?.buyo.connected ? 'done' : 'next',
      tag: integ.data?.buyo.connected ? 'Готово' : 'Подключить',
    },
    {
      title: 'Реклама Meta',
      text: 'Кабинеты, кампании и расход через Marketing API. Нужен токен системного пользователя.',
      state: integ.data?.meta.connected ? 'done' : 'next',
      tag: integ.data?.meta.connected ? 'Готово' : 'Подключить',
    },
    {
      title: 'Telegram',
      text: 'Алерты, апрувы и новые лиды — сразу в телефон. Нужен бот от @BotFather.',
      state: integ.data?.telegram.bound ? 'done' : 'next',
      tag: integ.data?.telegram.bound ? 'Готово' : 'Подключить',
    },
  ];
  const done = steps.filter((s) => s.state === 'done').length;
  const tone = (s: Step): Tone =>
    s.state === 'done' ? 'success' : s.state === 'next' ? 'warning' : 'default';
  return (
    <div className="sum-first">
      <Card
        title="Первый запуск"
        action={
          <span className="muted tnum" style={{ fontSize: 'var(--fs-sm)' }}>
            {done} из {steps.length} подключено
          </span>
        }
      >
        <p className="muted" style={{ margin: '0 0 14px', maxWidth: '60ch' }}>
          Когда подключим сеть и рекламу, здесь появятся вывод агента за утро, что ждёт вашего решения, и
          расход, лиды, апрувы, прибыль и прогноз за день.
        </p>
        <ol className="steps-list">
          {steps.map((s) => (
            <li key={s.title}>
              <span className={`step-mark${s.state === 'done' ? ' done' : ''}`}>
                {s.state === 'done' ? <Check size={15} /> : <Circle size={10} />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <b style={{ fontWeight: 600 }}>{s.title}</b>
                  <Chip tone={tone(s)}>{s.tag}</Chip>
                </div>
                <p className="muted" style={{ margin: '3px 0 0', fontSize: 'var(--fs-sm)' }}>
                  {s.text}
                </p>
              </div>
            </li>
          ))}
        </ol>
        <div style={{ marginTop: 14 }}>
          <Link to="/settings" hash="integrations" className="btn btn-primary">
            Открыть интеграции
          </Link>
        </div>
      </Card>
      <Waiting />
    </div>
  );
}
